<template>
    <div class="common-layout layout-root">
        <!-- Sticky Header -->
        <div class="header-sticky">
            <HeaderLogo
                :groupName="initData.groupName"
                :currentUrl="currentUrl"
            />
        <HeaderBar
            @save="handleSave"
            @openPdf="handleOpenPdf"
            @applyUpdates="handleApplyUpdates" 
            :hasUpdates="hasUpdates"
            :isUpdating="isUpdating"
            :vacancyData="initData.vacancyData"
            :consultantData="initData.consultantData"
            :candidateData="initData.candidateData"
            :profileId="initData.profileId"
            :senderId="initData.senderId"
            :senderEmail="initData.senderEmail"
            :leftTextWrapperMinWidth="'40%'"
            :saveButtonText="$trans('profile_dossier.save_dossier')"
            :readOnly="initData.initialCvData.read_only"
            :deleteUrl="initData.deleteUrl"
        />
        </div>

        <!-- Main Content -->
        <div class="layout-main">
            <!-- Sticky Left Menu -->
            <div class="menu-sticky">
                <SectionsMenu
                    :sections="sections"
                    :activeSection="activeSection"
                    :mainNavigationUrl="initData.mainNavigationUrl"
                    @select="setActiveSection"
                />
            </div>

            <!-- Editor + Preview -->
            <div class="editor-preview-wrapper">
                <div class="editor-area">
                    <SectionEditor
                        :activeSection="activeSection"
                        @savePhoto="handleImageSave"
                        @photoChange="handleFileChange"
                        @useDefaultPhoto="handleSaveDefaultPhoto"
                        @openVersionsModal="openVersionsModal"
                        :sectionContent="cvData[activeSection]"
                        v-model:sectionContent="sectionContent"
                        @updateContent="onUpdateContent"
                        :profileId="initData.profileId"
                        :dossierId="initData.dossierId"
                        :readOnly="initData.initialCvData.read_only"
                        :profileLanguage="initData.profileLanguage"
                        :appLocale="initData.appLocale"
                        :defaultPhoto="initData.defaultPhoto"
                        ref="sectionEditor"
                    />
                </div>

                <div class="preview-area">
                    <CvPreview
                        :cvData="cvData"
                        :activeSection="activeSection"
                        :paginatedSections="paginatedSections"
                        :profileName="initData.profileName"
                        :profileLanguage="initData.profileLanguage"
                        :groupName="initData.dossierGroupName"
                        :consultantData="initData.consultantData"
                        :dossierDate="initData.dossierDate"
                    />
                </div>
            </div>
        </div>

        <VersionSummaryModal ref="historyModal" />
    </div>
</template>

<script>
import { useCvStore } from "@/stores/ProfileDossierStore";
import { mapState, mapActions } from "pinia";
import { translations } from "./Locales/translations";

import axios from "axios";
import HeaderLogo from "./Components/HeaderLogo";
import HeaderBar from "./Components/HeaderBar.vue";
import SectionsMenu from "./Components/SectionsMenu";
import CvPreview from "./Components/CvPreview";
import SectionEditor from "./Components/SectionEditor";
import VersionSummaryModal from "./Components/VersionSummaryModal.vue";

export default {
    components: {
        HeaderLogo,
        HeaderBar,
        SectionsMenu,
        CvPreview,
        SectionEditor,
        VersionSummaryModal,
    },
    name: "Show",
    data() {
        return {
            sections: [
                "photo",
                "title",
                "competences",
                "languages",
                "birth_year",
                "region_residence",
                "summary",
                "employment",
                "education",
            ],
            activeSection: "photo",
            sectionContent: "",
            selectedFile: null,
            hasUpdates: this.initData.hasUpdates || false,
            isUpdating: false,
        };
    },
    props: {
        groupName: String,
        dossierGroupName: String,
        initData: Object,
        profileId: Number,
        vacancyData: Array,
    },
    mounted() {
        this.$functions.hideLoader();
    },
    computed: {
        ...mapState(useCvStore, ["cvData"]),
        paginatedSections() {
            const t =
                translations[this.initData.profileLanguage] ||
                translations["de"];

            const p = (text) => (text?.trim() ? `<p>${text}</p>` : "");

            const renderers = {
                competences: (items, activeSection) => {
                    const highlightClass =
                        activeSection === "competences" ? " highlight" : "";
                    const selected = (items || []).filter(
                        (item) => item.is_selected,
                    );
                    if (!selected.length)
                        return `<div class="competences-list${highlightClass}"><span>${t.competences}:</span></div>`;
                    const content = selected
                        .sort((a, b) => (b.duration || 0) - (a.duration || 0))
                        .map((i) => `${i.name || ""}, ${i.duration || "n/a"}`)
                        .join(" | ");
                    return `<div class="competences-list${highlightClass}"><span>${t.competences}:</span> ${content}</div>`;
                },
                languages: (items, activeSection) => {
                    const highlightClass =
                        activeSection === "languages" ? " highlight" : "";
                    const selected = (items || []).filter(
                        (item) => item.is_selected,
                    );

                    if (!selected.length) {
                        return `<div class="languages-content${highlightClass}" style="height: 20px;"></div>`;
                    }

                    const content = selected
                        .map((i) => {
                            const langName =
                                this.initData.profileLanguage === "de"
                                    ? i.name_de || i.name || t.unknownLanguage
                                    : i.name || i.name_de || t.unknownLanguage;

                            return `${langName} (${i.level_name || t.unknownLevel})`;
                        })
                        .join(", ");

                    return `<div class="languages-content${highlightClass}"><span>${t.languages}:</span> ${content}</div>`;
                },
                birth_year: (year, activeSection) => {
                    const highlightClass =
                        activeSection === "birth_year" ? " highlight" : "";
                    if (!year)
                        return `<div class="birth-year-content${highlightClass}" style="height: 20px;"></div>`;
                    return `<div class="birth-year-content${highlightClass}"><span>${t.yearOfBirth}:</span> ${year}</div>`;
                },
                region_residence: (region, activeSection) => {
                    const highlightClass =
                        activeSection === "region_residence"
                            ? " highlight"
                            : "";
                    if (!region?.trim())
                        return `<div class="region-residence-content${highlightClass}" style="height: 20px;"></div>`;
                    return `<div class="region-residence-content${highlightClass}"><span>${t.regionOfResidence}:</span> ${region}</div>`;
                },
                summary: (text, activeSection) => {
                    if (!text?.trim()) return null;
                    const paras = text
                        .split("\n")
                        .map((l) => l.trim())
                        .filter(Boolean);
                    if (!paras.length) return null;
                    const content = paras.map((p) => `<p>${p}</p>`).join("");
                    const highlightClass =
                        activeSection === "summary" ? " highlight" : "";
                    return `<div class="summary-content${highlightClass}">${content}</div>`;
                },
                employment: (items, activeSection) => {
                    const selected = (items || []).filter(
                        (item) => item.is_selected,
                    );
                    if (!selected.length) return null;
                    return selected
                        .map((item, idx) => {
                            const num = String(idx + 1).padStart(2, "0");
                            const highlightClass =
                                activeSection === "employment"
                                    ? " highlight"
                                    : "";
                            const jobDescription = p(item.job_description);
                            const orgText = item.organization_name
                                ? ` ${t.for} ${item.organization_name}`
                                : "";
                            const toDate =
                                !item.to || item.to === "-"
                                    ? t.ongoing
                                    : this.formatDate(item.to);

                            let durationLine = "";
                            if (item.from) {
                                durationLine = `<p>${t.duration} – ${this.formatDate(item.from)} – ${toDate}, ${item.duration_dossier || "n/a"}</p>`;
                            }

                            let locationLine = "";
                            if (item.city || item.country) {
                                const city = item.city || t.notSpecified;
                                const country = item.country || t.notSpecified;
                                locationLine = `<p>${t.location} – ${city} | ${country}</p>`;
                            }

                            return `<div class="employment-content${highlightClass}"><h3 style="color:#41abc4; margin:0; margin-top: 10px;">${t.employmentHeader(
                                num,
                            )}</h3><p><span style="font-weight: bold;">${
                                item.position_title || t.unknownPosition
                            }</span>${orgText}</p>${durationLine}${locationLine}${this.stripHtml(
                                jobDescription,
                            )}</div>`;
                        })
                        .join("");
                },
                education: (items, activeSection) => {
                    const selected = (items || []).filter(
                        (item) => item.is_selected,
                    );
                    if (!selected.length) return null;

                    return selected
                        .map((item, idx) => {
                            const num = String(idx + 1).padStart(2, "0");
                            const highlightClass =
                                activeSection === "education"
                                    ? " highlight"
                                    : "";

                            const instituteText = item.institute_name
                                ? ` (${item.institute_name})`
                                : "";
                            const toDate =
                                !item.to || item.to === "-"
                                    ? t.ongoing
                                    : this.formatDate(item.to);

                            let durationLine = "";
                            if (item.from) {
                                durationLine = `<p>${t.educationDuration} – ${this.formatDate(item.from)} – ${toDate} | ${item.duration || "n/a"}</p>`;
                            }

                            return `<div class="education-content${highlightClass}"><h3 style="color:#aa916a; margin:0;">${t.educationHeader(
                                num,
                            )}</h3><p>${
                                item.major || ""
                            }${instituteText}</p>${durationLine}<p>${t.educationLevel} – ${
                                item.education_level_description ||
                                t.unknownLevel
                            }</p></div>`;
                        })
                        .join("");
                },
            };

            const sectionsOrder = [
                "competences",
                "languages",
                "birth_year",
                "region_residence",
                "summary",
                "employment",
                "education",
            ];

            let fullHtmlContent = "";
            sectionsOrder.forEach((sectionName) => {
                const data = this.cvData[sectionName];
                const rendered = renderers[sectionName](
                    data,
                    this.activeSection,
                );
                if (!rendered) return;
                const blocks = Array.isArray(rendered) ? rendered : [rendered];
                fullHtmlContent += blocks.join("");
            });

            const contentPage = {
                section: "ContentPage",
                content: fullHtmlContent,
            };

            return [
                {
                    section: "CoverPage",
                    content: {
                        photo: this.cvData.photo || "",
                        title: this.cvData.title || "",
                    },
                },
                contentPage,
            ];
        },
    },
    watch: {
        activeSection: {
            immediate: true,
            handler(newSection) {
                if (newSection === "photo") {
                    this.sectionContent = "";
                } else {
                    this.sectionContent = this.cvData[newSection] || "";
                }
            },
        },
        sectionContent(newValue) {
            if (this.activeSection !== "photo") {
                this.updateSection(this.activeSection, newValue);
            }
        },
    },
    created() {
        if (this.initData.initialCvData) {
            this.initializeCvData();
        }
    },
    methods: {
        ...mapActions(useCvStore, ["updateSection"]),
        openVersionsModal() {
            if (this.$refs.historyModal) {
                this.$refs.historyModal.open(this.initData.dossierId);
            }
        },

        onUpdateContent(newContent) {
            this.updateSection(this.activeSection, newContent);
        },
        onUpdateContent(newContent) {
            this.updateSection(this.activeSection, newContent);
        },
        stripHtml(html) {
            if (!html) return "";
            const div = document.createElement("div");
            div.innerHTML = String(html);
            let text = div.textContent || div.innerText || "";

            text = text.replace(/\u00A0/g, " ").trim();

            return text;
        },
        formatDate(date) {
            if (!date || date === "-") {
                const today = new Date();
                const month = String(today.getMonth() + 1).padStart(2, "0");
                const year = today.getFullYear();
                return `${month}.${year}`;
            }

            const parts = date.split("-");
            if (parts.length >= 2) {
                return `${parts[1]}.${parts[0]}`;
            }

            return date;
        },
        initializeCvData() {
            for (const [section, value] of Object.entries(
                this.initData.initialCvData,
            )) {
                const normalizedValue =
                    value &&
                    typeof value === "object" &&
                    Array.isArray(value.data)
                        ? value.data
                        : value;
                this.updateSection(section, normalizedValue);
            }
        },
        setActiveSection(section) {
            this.activeSection = section;

            this.$nextTick(() => {
                let selector = null;

                if (section === "photo" || section === "title") {
                    const pageIndex = this.paginatedSections.findIndex(
                        (page) => page.section === "CoverPage",
                    );
                    if (pageIndex !== -1) {
                        selector = "#section-page-" + pageIndex;
                    }
                } else {
                    const sectionToClassMap = {
                        competences: ".competences-list",
                        languages: ".languages-content",
                        birth_year: ".birth-year-content",
                        region_residence: ".region-residence-content",
                        summary: ".summary-content",
                        employment: ".employment-content",
                        education: ".education-content",
                    };
                    const classSelector = sectionToClassMap[section];
                    if (classSelector) {
                        selector = classSelector;
                    } else {
                        const pageIndex = this.paginatedSections.findIndex(
                            (page) => page.sectionName === section,
                        );
                        if (pageIndex !== -1) {
                            selector = "#section-page-" + pageIndex;
                        }
                    }
                }

                if (selector) {
                    const isLastSection = section === "education";
                    const el = document.querySelector(selector);

                    if (el) {
                        if (isLastSection) {
                            const scrollContainer =
                                document.querySelector(".preview-area");
                            if (scrollContainer && el) {
                                const containerRect =
                                    scrollContainer.getBoundingClientRect();
                                const elementRect = el.getBoundingClientRect();
                                const offset =
                                    elementRect.top - containerRect.top;
                                const newScrollTop =
                                    scrollContainer.scrollTop + offset;

                                scrollContainer.scrollTo({
                                    top: newScrollTop,
                                    behavior: "smooth",
                                });
                            }
                        } else {
                            el.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                            });
                        }
                    }
                }

                this.updateHighlightClass();
            });
        },
        updateHighlightClass() {
            const classesToClear = ["competences-list"];
            classesToClear.forEach((className) => {
                const els = document.querySelectorAll(`.${className}`);
                els.forEach((el) => el.classList.remove("highlight"));
            });

            const sectionClassMap = {
                competences: "competences-list",
            };

            const classToHighlight = sectionClassMap[this.activeSection];
            if (classToHighlight) {
                const els = document.querySelectorAll(`.${classToHighlight}`);
                els.forEach((el) => el.classList.add("highlight"));
            }
        },
        beforeUpload(file) {
            const isImage = file.type.startsWith("image/");
            if (!isImage) {
                this.$message.error("Only graphic files allowed.");
                return false;
            }
            this.selectedFile = file;
            return false;
        },
        async handleImageSave() {
            if (!(this.selectedFile instanceof File)) {
                this.$message.warning("First, select photo!");
                return;
            }

            const formData = new FormData();
            formData.append("photo", this.selectedFile);

            const token = document.head.querySelector(
                'meta[name="csrf-token"]',
            )?.content;

            try {
                this.$functions.showLoader();
                const dossierId = this.initData.dossierId;
                const response = await axios.post(
                    `/profile-dossier/${dossierId}/process-image`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            "X-CSRF-TOKEN": token,
                        },
                    },
                );

                if (response.data?.base64) {
                    this.updateSection("photo", response.data.base64);
                    this.$message.success(
                        this.$trans("profile_dossier.photo_saved_successfully"),
                    );

                    if (this.$refs.sectionEditor) {
                        this.$refs.sectionEditor.clearUploaderFiles();
                    }
                    this.selectedFile = null;
                } else {
                    this.$message.error("Error while processing image.");
                }

                this.$functions.hideLoader();
            } catch (error) {
                console.error(error);
                this.$message.error("Error while sending.");
                this.$functions.hideLoader();
            }
        },
        async handleSaveDefaultPhoto(useDefault) {
            const token = document.head.querySelector(
                'meta[name="csrf-token"]',
            )?.content;

            try {
                this.$functions.showLoader();
                const dossierId = this.initData.dossierId;
                const payload = {
                    use_default_photo: useDefault,
                };

                const response = await axios.post(
                    `/profile-dossier/${dossierId}/set-default-image`,
                    payload,
                    {
                        headers: {
                            "X-CSRF-TOKEN": token,
                            "Content-Type": "application/json",
                        },
                    },
                );

                if (response.data?.base64) {
                    this.updateSection("photo", response.data.base64);

                    if (useDefault) {
                        this.$message.success(
                            this.$trans(
                                "profile_dossier.photo_set_to_default_successfully",
                            ),
                        );
                    } else {
                        this.$message.success(
                            this.$trans("profile_dossier.photo_set_to_custom"),
                        );
                    }

                    if (this.$refs.sectionEditor) {
                        this.$refs.sectionEditor.clearUploaderFiles();
                    }

                    this.selectedFile = null;
                } else {
                    this.$message.error("Error while processing image.");
                }
            } catch (error) {
                console.error(error);
                this.$message.error("Error while sending.");
            } finally {
                this.$functions.hideLoader();
            }
        },
        handleFileChange(file) {
            this.selectedFile = file;
        },
        handleExceed(files, fileList) {
            this.$message.warning("You can send only one image.");
        },
        async handleSave() {
            const jsonData = {
                photo: this.cvData["photo"],
                title: this.cvData["title"],
                summary: this.cvData["summary"],
                birth_year: this.cvData["birth_year"],
                region_residence: this.cvData["region_residence"],
                competences: this.cvData["competences"].map(
                    ({ id, is_selected }) => ({
                        id,
                        is_selected,
                    }),
                ),
                employment: this.cvData["employment"].map(
                    ({ id, is_selected }) => ({
                        id,
                        is_selected,
                    }),
                ),
                education: this.cvData["education"].map(
                    ({ id, is_selected }) => ({
                        id,
                        is_selected,
                    }),
                ),
                languages: this.cvData["languages"].map(
                    ({ id, is_selected }) => ({
                        id,
                        is_selected,
                    }),
                ),
            };

            const token = document.head.querySelector(
                'meta[name="csrf-token"]',
            )?.content;
            const dossierId = this.initData.dossierId;

            try {
                await axios.put(`/profile-dossier/${dossierId}`, jsonData, {
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": token,
                    },
                });
                this.$message.success("Dossier saved successfully.");
            } catch (error) {
                const errorMessage =
                    error.response?.data?.message ||
                    error.message ||
                    "Unknown error";
                this.$message.error(
                    `Error while saving dossier: ${errorMessage}`,
                );
            }
        },
        handleOpenPdf() {
            if (this.initData.viewPdfUrl) {
                window.open(this.initData.viewPdfUrl, "_blank");
            } else {
                this.$message.error(`Error`);
            }
        },
        async handleApplyUpdates() {
            this.$functions.showLoader();
            this.isUpdating = true;
            
            try {
                const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                const response = await axios.post(`/profile-dossier/${this.initData.dossierId}/apply-updates`, {}, {
                    headers: {
                        'X-CSRF-TOKEN': token
                    }
                });
                if (response.data.success) {
                    this.hasUpdates = false;
                    this.$message.success(response.data.message || "Updated!");
                    
                    window.location.reload();
                }
            } catch (error) {
                this.$message.error("Someting went wrong during update.");
                console.error(error);
            } finally {
                this.isUpdating = false;
                this.$functions.hideLoader();
            }
        }
    },
};
</script>

<style>
.layout-root {
    height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: "Barlow", sans-serif;
}

.header-sticky {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
}

.layout-main {
    flex: 1;
    display: flex;
    overflow: hidden;
    background-color: #ededed;
}

.menu-sticky {
    position: sticky;
    height: calc(100vh - 80px);
    z-index: 9;
    background-color: #ededed;
}

.editor-preview-wrapper {
    flex: 1;
    display: flex;
}

.editor-area {
    flex: 1;
    overflow: auto;
}

.preview-area {
    flex: 1;
    overflow-y: auto;
}

.highlight {
    background-color: rgba(0, 120, 215, 0.2);
    border: 1px solid rgba(0, 120, 215, 0.8);
    border-radius: 2px;
}

.competences-list > span {
    font-size: 18px;
    font-weight: 600;
}

.languages-content > span,
.birth-year-content > span,
.region-residence-content > span {
    font-size: 18px;
    font-weight: 600;
}

.version-preview {
    max-height: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    font-size: 0.9em;
    color: #606266;
}
</style>
